import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import fetchWithAuth from "../../lib/fetchWithAuth";
import { API_BASE_URL } from "../../config";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);


interface ContestInfo {
    id: number;
    contest_name: string;
    no_of_questions: number;
}



const CreateQuestion: React.FC = () => {
    const { contestId } = useParams<{ contestId: string }>();
    const navigate = useNavigate();
    const [contest, setContest] = useState<ContestInfo | null>(null);
    const [createdCount, setCreatedCount] = useState<number | null>(null);
    const [formData, setFormData] = useState({
        question_text: "",
        option_a: "",
        option_b: "",
        option_c: "",
        option_d: "",
        correct_option: "",
    });

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const fields = [
            formData.question_text,
            formData.option_a,
            formData.option_b,
            formData.option_c,
            formData.option_d,
            formData.correct_option,
        ];

        const anyEmpty = fields.some((f) => !f || f.trim() === "");
        if (anyEmpty) {
            await MySwal.fire({
                title: "Missing Fields",
                text: "Please fill out all fields before submitting.",
                icon: "warning",
                confirmButtonText: "OK",
                customClass: {
                    popup: "rounded-xl shadow-md",
                    confirmButton:
                        "bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600",
                },
            });
            return;
        }

        try {
            const res = await fetchWithAuth(`${API_BASE_URL}/admin/questions`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...formData,
                    contest_id: parseInt(contestId ?? "0"),
                }),
            });

            if (!res.ok) throw new Error("Failed to create question");

            await MySwal.fire({
                title: "Success",
                text: "Question created successfully!",
                icon: "success",
                confirmButtonText: "OK",
                customClass: {
                    popup: "rounded-xl shadow-md",
                    confirmButton:
                        "bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700",
                },
            });

            // Reset form
            setFormData({
                question_text: "",
                option_a: "",
                option_b: "",
                option_c: "",
                option_d: "",
                correct_option: "",
            });
        } catch (err) {
            console.error("Failed to create question:", err);
            await MySwal.fire({
                title: "Error",
                text: "Failed to create question. Try again later.",
                icon: "error",
                confirmButtonText: "OK",
                customClass: {
                    popup: "rounded-xl shadow",
                    confirmButton:
                        "bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700",
                },
            });
        }
    };

    useEffect(() => {
        const loadData = async () => {
            try {
                if (!contestId) return;
                const [contestRes, questionsRes] = await Promise.all([
                    fetchWithAuth(`${API_BASE_URL}/admin/contest/${contestId}`),
                    fetchWithAuth(`${API_BASE_URL}/admin/questions`),
                ]);

                const contestData = await contestRes.json();
                const questionsData = await questionsRes.json();

                setContest(contestData.contest);

                const count = questionsData.questions.filter(
                    (q: any) => q.contest_id === parseInt(contestId)
                ).length;

                setCreatedCount(count);
            } catch (err) {
                console.error("Error loading contest data:", err);
                navigate("/admin/questions");
            }
        };

        loadData();
    }, [contestId, navigate]);

    if (!contest || createdCount === null) {
        return <div className="p-6 text-center text-lg">Loading contest info...</div>;
    }

    const isComplete = createdCount >= contest.no_of_questions;

    if (isComplete) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="bg-white shadow-lg rounded-xl p-8 max-w-md text-center">
                    <h2 className="text-2xl font-bold text-green-600 mb-2">✅ All Questions Created!</h2>
                    <p className="text-gray-700 mb-4">
                        You've successfully created all {contest.no_of_questions} questions for
                        <br />
                        <span className="font-semibold text-indigo-600">{contest.contest_name}</span>.
                    </p>
                    <button
                        onClick={() => navigate("/admin/questions")}
                        className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 transition"
                    >
                        🔙 Back to Manage Questions
                    </button>
                </div>
            </div>
        );
    }
    return (
        <div className="p-6 max-w-3xl mx-auto">
            <h1 className="text-2xl font-bold mb-2">Create Question</h1>
            <p className="text-sm text-gray-600 mb-4">
                {contest?.contest_name} (Created: {createdCount}/{contest?.no_of_questions})
            </p>

            {contest && createdCount >= contest.no_of_questions ? (
                <div className="text-center py-20 bg-gray-50 rounded-lg shadow">
                    <h2 className="text-xl font-semibold text-red-600 mb-2">
                        🎉 All Questions Created
                    </h2>
                    <p className="text-gray-700">
                        You've already created all {contest.no_of_questions} questions for this contest.
                    </p>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-4 bg-white shadow p-6 rounded">
                    <div>
                        <label className="block text-sm font-medium">Question Text</label>
                        <input
                            type="text"
                            name="question_text"
                            value={formData.question_text}
                            onChange={handleChange}
                            className="w-full border p-2 rounded"
                            required
                        />
                    </div>
                    {["option_a", "option_b", "option_c", "option_d"].map((key) => (
                        <div key={key}>
                            <label className="block text-sm font-medium capitalize">
                                {key.replace("_", " ")}
                            </label>
                            <input
                                type="text"
                                name={key}
                                value={(formData as any)[key]}
                                onChange={handleChange}
                                className="w-full border p-2 rounded"
                                required
                            />
                        </div>
                    ))}
                    <div>
                        <label className="block text-sm font-medium">Correct Option</label>
                        <select
                            name="correct_option"
                            value={formData.correct_option}
                            onChange={handleChange}
                            className="w-full border p-2 rounded"
                            required
                        >
                            <option value="">Select</option>
                            <option value="a">A</option>
                            <option value="b">B</option>
                            <option value="c">C</option>
                            <option value="d">D</option>
                        </select>
                    </div>

                    <div className="pt-4 text-right">
                        <button
                            type="submit"
                            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                        >
                            ➕ Save Question
                        </button>
                    </div>
                </form>
            )}
        </div>
    );


};

export default CreateQuestion;
